import useIsReadOnlyMode from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import SmartLabel from 'components/v2/labels/SmartLabel';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PCRModel } from 'services/EpdClient';
import FileService from 'services/FileService';
import CPcrSelectorDialog from './CPcrSelectorDialog';
import PcrSelectorFilesDialog from '../pcr-selector/PcrSelectorFilesDialog';
import SelectorButton from 'components/v2/buttons/SelectorButton';

interface CPcrSelectorProps {
  label?: string | undefined;
  tooltip?: string | undefined;
  tooltipPosition?: any | undefined;
  cpcr: PCRModel | null;
  cpcrList: PCRModel[];
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  onChangeCPcr: (pcr: PCRModel | null) => void;
  disclaimerText?: React.ReactNode;
}

const CPcrSelector: React.FunctionComponent<CPcrSelectorProps> = ({
  label,
  tooltip,
  tooltipPosition,
  cpcr,
  cpcrList,
  disabled,
  required,
  onChangeCPcr,
  disclaimerText,
}) => {
  const { t } = useTranslation();
  const [showFiles, setShowFiles] = useState<boolean>(false);
  const [isEditOpened, setIsEditOpened] = useState<boolean>(false);
  const isReadOnly = useIsReadOnlyMode() || disabled;

  const handleCloseDialog = () => {
    setIsEditOpened(false);
  };

  const handleDownloadSelectedPcrFiles = (pcr: PCRModel | undefined) => {
    if (pcr?.documents && pcr.documents?.length > 1) {
      setShowFiles(true);
    } else if (pcr?.documents && pcr.documents?.length === 1) {
      window.open(FileService.getImageUrl(pcr.documents[0].id || ''), '_blank', 'noreferrer');
    }
  };

  const handleRemoveCpcr = () => {
    onChangeCPcr(null);
  };

  return (
    <>
      {label && (
        <SmartLabel
          label={label}
          required={required}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={!!cpcr}
        />
      )}

      <SelectorButton
        name="cpcr"
        value={cpcr ? cpcr.fullName : ''}
        placeholder="Add C-PCR"
        addButtonText="+ Add C-PCR"
        addButtonDisabled={disabled || !!cpcr}
        onOpenSelector={() => setIsEditOpened(true)}
        showDeleteButton={!!cpcr && !isReadOnly}
        onDeleteClicked={handleRemoveCpcr}
        showDownloadButton={!!cpcr}
        onDownloadClicked={() => handleDownloadSelectedPcrFiles(cpcr!)}
        downloadButtonDisabled={cpcr?.documents && cpcr.documents.length === 0}
        disabled={isReadOnly}
      />

      <CPcrSelectorDialog
        visible={isEditOpened}
        onHide={handleCloseDialog}
        cpcrList={cpcrList}
        initialCpcr={cpcr}
        onCpcrChange={onChangeCPcr}
        disclaimerText={disclaimerText}
      />

      <PcrSelectorFilesDialog
        pcrType="cpcr"
        isOpened={showFiles}
        onHide={() => {
          setShowFiles(false);
        }}
        files={cpcr?.documents ?? []}
      />
    </>
  );
};

export default CPcrSelector;
